/**
 * Vocabolario degli status protetti — modulo **puro** (nessuna dipendenza dal layer HTTP).
 *
 * Sta a parte da `protectedStatusApi.ts` perché lo usa anche `leaveApi` per l'editor dei tipi
 * di assenza: `leave-type.requiredProtectedStatusTypes` è un CSV di questi codici, e il BE lo
 * valida voce per voce (`LEAVE_INVALID_REQUIRED_PROTECTED_STATUS_TYPE` con `invalidValues`).
 * Farlo scegliere da un elenco, invece che digitare, evita il refuso che spegne un tipo di
 * assenza per l'intera azienda in silenzio.
 *
 * Copia di `ProtectedStatusVocab` del BE: se divergono vince il BE.
 */

export interface StatusGroup { label: string; types: string[] }

/** Gruppi nell'ordine del BE (§2.1 → §2.7). */
export const STATUS_GROUPS: StatusGroup[] = [
  { label: 'Disabilità e categorie protette', types: ['law-104-self', 'law-104-caregiver', 'law-68-disabled', 'oncology-serious', 'chronic-serious-illness'] },
  { label: 'Genitorialità', types: ['maternity-mandatory', 'maternity-early', 'paternity-mandatory', 'parental-leave', 'child-illness-leave', 'assisted-fertilization', 'voluntary-termination-pregnancy'] },
  { label: 'Rappresentanze e cariche', types: ['union-rep-rsu', 'union-rep-rsa', 'safety-rep-rls', 'elected-office', 'civil-court-lay-judge', 'witness-court', 'electoral-officer'] },
  { label: 'Volontariato e servizio pubblico', types: ['blood-donor', 'civil-protection-volunteer', 'firefighter-volunteer', 'military-reservist', 'civil-service', 'red-cross-volunteer'] },
  { label: 'Formazione', types: ['student-worker', 'sabbatical'] },
  { label: 'Occorrenze puntuali', types: ['marriage-leave', 'bereavement', 'condominium-admin'] },
  { label: 'Situazioni tutelate speciali', types: ['domestic-violence-victim', 'whistleblower'] },
];

export const STATUS_TYPE_LABEL: Record<string, string> = {
  'law-104-self': 'L.104 — sé stesso',
  'law-104-caregiver': 'L.104 — caregiver',
  'law-68-disabled': 'L.68 — categoria protetta',
  'oncology-serious': 'Patologia oncologica grave',
  'chronic-serious-illness': 'Malattia cronica grave',
  'maternity-mandatory': 'Maternità obbligatoria',
  'maternity-early': 'Maternità anticipata',
  'paternity-mandatory': 'Paternità obbligatoria',
  'parental-leave': 'Congedo parentale',
  'child-illness-leave': 'Malattia del figlio',
  'assisted-fertilization': 'Procreazione assistita',
  'voluntary-termination-pregnancy': 'Interruzione volontaria di gravidanza',
  'union-rep-rsu': 'Rappresentante sindacale RSU',
  'union-rep-rsa': 'Rappresentante sindacale RSA',
  'safety-rep-rls': 'Rappresentante sicurezza RLS',
  'elected-office': 'Carica elettiva',
  'civil-court-lay-judge': 'Giudice onorario',
  'witness-court': 'Testimone in giudizio',
  'electoral-officer': 'Componente di seggio',
  'blood-donor': 'Donatore di sangue',
  'civil-protection-volunteer': 'Volontario protezione civile',
  'firefighter-volunteer': 'Vigile del fuoco volontario',
  'military-reservist': 'Riservista militare',
  'civil-service': 'Servizio civile',
  'red-cross-volunteer': 'Volontario Croce Rossa',
  'student-worker': 'Studente lavoratore',
  sabbatical: 'Congedo per formazione',
  'marriage-leave': 'Congedo matrimoniale',
  bereavement: 'Lutto',
  'condominium-admin': 'Amministratore di condominio',
  'domestic-violence-victim': 'Vittima di violenza domestica',
  whistleblower: 'Segnalante (whistleblowing)',
};

/** Sottotipi ammessi per status: array vuoto = sottotipo libero o non previsto. */
export const SUB_TYPES_BY_STATUS: Record<string, string[]> = {
  'law-104-self': ['mild', 'severe'],
  'law-104-caregiver': ['severe'],
  'law-68-disabled': ['civil-invalid', 'category-a-victims', 'category-b-invalid-work', 'category-b-invalid-service', 'category-b-invalid-war'],
  'oncology-serious': ['active-treatment', 'remission'],
  'chronic-serious-illness': [],
  'maternity-mandatory': ['pre-childbirth', 'post-childbirth', 'flexible'],
  'maternity-early': ['pregnancy-risk', 'workplace-risk'],
  'paternity-mandatory': [],
  'parental-leave': ['mother', 'father', 'single-parent'],
  'child-illness-leave': [],
  'assisted-fertilization': [],
  'voluntary-termination-pregnancy': [],
  'union-rep-rsu': [], 'union-rep-rsa': [], 'safety-rep-rls': [],
  'elected-office': ['municipal', 'provincial', 'regional', 'national', 'european'],
  'civil-court-lay-judge': [],
  'witness-court': ['civil', 'criminal', 'admin'],
  'electoral-officer': ['president', 'secretary', 'scrutineer'],
  'blood-donor': ['avis-cri', 'occasional', 'bone-marrow'],
  'civil-protection-volunteer': [], 'firefighter-volunteer': [], 'military-reservist': [],
  'civil-service': [], 'red-cross-volunteer': [],
  'student-worker': ['university', 'high-school', 'professional'],
  sabbatical: [],
  'marriage-leave': ['civil-marriage', 'civil-partnership', 'religious-marriage'],
  bereavement: ['spouse-partner', 'parent', 'child', 'sibling', 'grandparent', 'in-law-parent'],
  'condominium-admin': [],
  'domestic-violence-victim': [], whistleblower: [],
};

export const SUB_TYPE_LABEL: Record<string, string> = {
  mild: 'Non grave', severe: 'Grave (art. 3 c. 3)',
  'civil-invalid': 'Invalido civile', 'category-a-victims': 'Categoria A — vittime',
  'category-b-invalid-work': 'Categoria B — invalido del lavoro',
  'category-b-invalid-service': 'Categoria B — invalido per servizio',
  'category-b-invalid-war': 'Categoria B — invalido di guerra',
  'active-treatment': 'In terapia attiva', remission: 'In remissione',
  'pre-childbirth': 'Ante partum', 'post-childbirth': 'Post partum', flexible: 'Flessibile',
  'pregnancy-risk': 'Rischio gravidanza', 'workplace-risk': 'Rischio sul lavoro',
  mother: 'Madre', father: 'Padre', 'single-parent': 'Genitore solo',
  municipal: 'Comunale', provincial: 'Provinciale', regional: 'Regionale', national: 'Nazionale', european: 'Europea',
  civil: 'Civile', criminal: 'Penale', admin: 'Amministrativo',
  president: 'Presidente', secretary: 'Segretario', scrutineer: 'Scrutatore',
  'avis-cri': 'AVIS / CRI', occasional: 'Occasionale', 'bone-marrow': 'Midollo osseo',
  university: 'Università', 'high-school': 'Scuola secondaria', professional: 'Formazione professionale',
  'civil-marriage': 'Matrimonio civile', 'civil-partnership': 'Unione civile', 'religious-marriage': 'Matrimonio religioso',
  'spouse-partner': 'Coniuge / partner', parent: 'Genitore', child: 'Figlio', sibling: 'Fratello / sorella',
  grandparent: 'Nonno / nonna', 'in-law-parent': 'Suocero / suocera',
};

export const RELATIONSHIPS = [
  'spouse', 'civil-partner', 'cohabiting-partner', 'child', 'parent',
  'grandparent', 'sibling', 'grandchild', 'in-law-parent', 'other',
] as const;
export const RELATIONSHIP_LABEL: Record<string, string> = {
  spouse: 'Coniuge', 'civil-partner': 'Unito civilmente', 'cohabiting-partner': 'Convivente',
  child: 'Figlio/a', parent: 'Genitore', grandparent: 'Nonno/a', sibling: 'Fratello/sorella',
  grandchild: 'Nipote', 'in-law-parent': 'Suocero/a', other: 'Altro',
};

/** Codice fiscale italiano, stessa regex del BE (tollerante sul case). */
export const FISCAL_CODE_RE = /^[A-Za-z]{6}\d{2}[A-Za-z]\d{2}[A-Za-z]\d{3}[A-Za-z]$/;

/** Claim aggiuntivi per i due status sensibili: essere HR non basta (§2.7). */
export const CLAIM_SENSITIVE_READ = 'hr.protected.sensitive.read';
export const CLAIM_SENSITIVE_WRITE = 'hr.protected.sensitive.write';
export const SENSITIVE_STATUS_TYPES = ['domestic-violence-victim', 'whistleblower'] as const;

/** Status in cui la persona tutelata è parte del presupposto giuridico (§4.3.3). */
export const REQUIRES_RELATED_PERSON = [
  'law-104-caregiver', 'parental-leave', 'child-illness-leave', 'bereavement',
  'maternity-mandatory', 'paternity-mandatory',
] as const;

/** Status irripetibili per rapporto di lavoro (§2.6). */
export const SINGLE_USE_STATUS_TYPES = ['marriage-leave'] as const;

/** Elenco piatto dei codici, nell'ordine dei gruppi (per select e validazioni). */
export const PROTECTED_STATUS_TYPES: readonly string[] = STATUS_GROUPS.flatMap((g) => g.types);
export const PROTECTED_STATUS_TYPE_LABELS = STATUS_TYPE_LABEL;

/** Elenco piatto e deduplicato dei sottotipi ammessi da almeno uno status. */
export const PROTECTED_SUB_TYPES: readonly string[] =
  [...new Set(Object.values(SUB_TYPES_BY_STATUS).flat())];
export const PROTECTED_SUB_TYPE_LABELS = SUB_TYPE_LABEL;

export const isSensitiveStatus = (t?: string | null): boolean =>
  Boolean(t && (SENSITIVE_STATUS_TYPES as readonly string[]).includes(t));
export const requiresRelatedPerson = (t?: string | null): boolean =>
  Boolean(t && (REQUIRES_RELATED_PERSON as readonly string[]).includes(t));
export const statusLabel = (t?: string | null) => (t ? STATUS_TYPE_LABEL[t] ?? t : '—');
export const subTypeLabel = (t?: string | null) => (t ? SUB_TYPE_LABEL[t] ?? t : null);
